import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  Download,
  Target,
  Users,
  TrendingUp,
  Euro
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useProposals } from '../../hooks/useSupabase';
import { supabase } from '../../lib/supabase';

// Helper to safely parse localized currency strings (e.g. "1.800" -> 1800, "1,800.50" -> 1800.50)
// Helper to safely parse localized currency strings (e.g. "1.800" -> 1800, "1,800.50" -> 1800.50, "2,400" -> 2400)
const parseCurrency = (value: any): number => {
  if (typeof value === 'number') return value;
  if (!value) return 0;

  let str = String(value).trim();

  // Remove anything that isn't a digit, dot, or comma
  str = str.replace(/[^0-9.,]/g, '');

  if (!str) return 0;

  // Complex detection for TR vs US formats

  // If both dot and comma exist
  if (str.includes('.') && str.includes(',')) {
    const dotIndex = str.lastIndexOf('.');
    const commaIndex = str.lastIndexOf(',');

    // Comma is last (1.800,50) -> TR format
    if (commaIndex > dotIndex) {
      str = str.replace(/\./g, '').replace(',', '.');
    }
    // Dot is last (1,800.50) -> US format
    else {
      str = str.replace(/,/g, '');
    }
  }
  // Single separator type (Only dot OR Only comma)
  else {
    const separator = str.includes('.') ? '.' : ',';
    const parts = str.split(separator);

    // Logic: 
    // 1. If multiple separators exist (1.234.567) -> Thousand separator
    // 2. If the last part has exactly 3 digits (1.800 or 2,400) -> Thousand separator
    // 3. Otherwise treat as decimal (1.5 or 10,50)

    if (parts.length > 2 || parts[parts.length - 1].length === 3) {
      // It is a thousand separator, remove it
      str = str.replace(new RegExp(`\\${separator}`, 'g'), '');
    } else {
      // It is a decimal separator
      if (separator === ',') {
        str = str.replace(',', '.');
      }
      // If separator is dot, it's already in JS valid format (1.5)
    }
  }

  return Number(str) || 0;
};

const SalesReports: React.FC = () => {
  const { user } = useAuth();
  const { proposals, loading: proposalsLoading, fetchProposals } = useProposals();
  const [selectedPeriod, setSelectedPeriod] = useState('last-30-days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [enrolledLeads, setEnrolledLeads] = useState<any[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);

  // Fetch proposals when component mounts
  useEffect(() => {
    fetchProposals();
  }, []);

  // Fetch enrolled leads that might not have proposals
  useEffect(() => {
    const fetchEnrolledLeads = async () => {
      if (!user) return;
      setLeadsLoading(true);
      try {
        const { data, error } = await supabase
          .from('leads')
          .select('*, creator:profiles!leads_created_by_fkey(branch_id)')
          .or('status.eq.enrolled,is_registered.eq.true');

        if (error) throw error;
        setEnrolledLeads(data || []);
      } catch (err) {
        console.error('Error fetching enrolled leads:', err);
      } finally {
        setLeadsLoading(false);
      }
    };

    fetchEnrolledLeads();
  }, [user]);

  // Combine and Process Data
  const { mergedAcceptedItems, mergedTotalItems } = React.useMemo(() => {
    // 1. Filter Proposals for current user OR branch
    const userProposals = proposals.filter(proposal => {
      const isOwner = proposal.createdBy === user?.name ||
        proposal.createdBy === user?.id ||
        proposal.created_by === user?.id ||
        proposal.created_by === user?.name ||
        proposal.createdBy === user?.email ||
        proposal.created_by === user?.email;

      // Note: We don't fetch creator's branch for proposals yet, but lead's branch is strong.
      // If we needed creator branch, we'd need to update useSupabase query too.
      // For now, relying on lead.branch_id is usually sufficient for proposals.
      const isBranch = user?.branchId && proposal.lead?.branch_id === user.branchId;

      return isOwner || isBranch;
    });

    const acceptedProposals = userProposals.filter(p => p.status === 'accepted');
    const acceptedProposalLeadIds = new Set(acceptedProposals.map(p => p.lead_id).filter(Boolean));

    // 2. Filter Enrolled Leads for current user OR branch
    const userEnrolledLeads = enrolledLeads.filter(lead => {
      const isCreator = lead.created_by === user?.id;
      const isAssigned = lead.assigned_consultants && Array.isArray(lead.assigned_consultants) && lead.assigned_consultants.includes(user?.id);

      const leadBranchId = lead.branch_id;
      // Access flatten joined data from Supabase if needed, or raw object structure
      // Supabase returns relations as objects: creator: { branch_id: ... }
      const creatorBranchId = lead.creator?.branch_id;

      const isBranch = user?.branchId && (leadBranchId === user.branchId || creatorBranchId === user.branchId);

      return isCreator || isAssigned || isBranch;
    });

    // 3. Identify Manual Leads (Enrolled but NO accepted proposal)
    const manualLeads = userEnrolledLeads.filter(lead => !acceptedProposalLeadIds.has(lead.id));

    // 4. Convert Manual Leads to "Proposal-like" items
    const manualLeadItems = manualLeads.map(lead => ({
      id: `manual_${lead.id}`,
      studentName: `${lead.first_name} ${lead.last_name}`,
      university: lead.interested_universities?.[0] || 'Manuel Kayıt',
      program: lead.interested_programs?.[0] || 'Genel',
      totalAmount: parseCurrency(lead.discussed_price), // Robust parsing
      createdAt: new Date(lead.created_at),
      respondedAt: new Date(lead.updated_at || lead.created_at),
      status: 'accepted',
      type: 'manual_lead'
    }));

    return {
      mergedAcceptedItems: [...acceptedProposals, ...manualLeadItems],
      mergedTotalItems: [...userProposals, ...manualLeadItems] // Manual leads count as 1 attempt & 1 win
    };
  }, [proposals, enrolledLeads, user]);


  // Calculate monthly performance data
  const monthlyPerformanceData = React.useMemo(() => {
    const monthlyData: { [key: string]: { month: string; proposals: number; accepted: number; revenue: number } } = {};

    // Sort items chronologically first to ensure months are in order if possible (rough fix)
    const sortedItems = [...mergedTotalItems].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    sortedItems.forEach(item => {
      const date = item.createdAt instanceof Date ? item.createdAt : new Date(item.createdAt);
      const month = date.toLocaleDateString('tr-TR', { month: 'short' });

      if (!monthlyData[month]) {
        monthlyData[month] = { month, proposals: 0, accepted: 0, revenue: 0 };
      }

      monthlyData[month].proposals += 1;

      if (item.status === 'accepted') {
        monthlyData[month].accepted += 1;
        monthlyData[month].revenue += parseCurrency(item.totalAmount); // Robust parsing
      }
    });

    return Object.values(monthlyData);
  }, [mergedTotalItems]);

  // Calculate stats
  const stats = React.useMemo(() => {
    const totalProposals = mergedTotalItems.length;
    const acceptedCount = mergedAcceptedItems.length;
    // Robust parsing for total revenue
    const monthlyRevenue = mergedAcceptedItems.reduce((sum, item) => sum + parseCurrency(item.totalAmount), 0);
    const averageDealSize = acceptedCount > 0 ? monthlyRevenue / acceptedCount : 0;
    const conversionRate = totalProposals > 0 ? (acceptedCount / totalProposals) * 100 : 0;

    // This month data
    const thisMonth = new Date();
    const thisMonthAccepted = mergedAcceptedItems.filter(item => {
      const itemDate = item.respondedAt instanceof Date ? item.respondedAt : new Date(item.respondedAt);
      return itemDate.getMonth() === thisMonth.getMonth() &&
        itemDate.getFullYear() === thisMonth.getFullYear();
    });

    // This month created (for targets)
    const thisMonthCreated = mergedTotalItems.filter(item => {
      const itemDate = item.createdAt instanceof Date ? item.createdAt : new Date(item.createdAt);
      return itemDate.getMonth() === thisMonth.getMonth() &&
        itemDate.getFullYear() === thisMonth.getFullYear();
    });

    // This month revenue
    const thisMonthRevenue = thisMonthAccepted.reduce((sum, item) => sum + parseCurrency(item.totalAmount), 0); // Robust parsing

    return {
      totalProposals,
      acceptedProposals: acceptedCount,
      monthlyRevenue,
      conversionRate: Math.round(conversionRate * 10) / 10,
      averageDealSize: Math.round(averageDealSize),
      pipelineValue: monthlyRevenue,
      closedDealsThisMonth: thisMonthAccepted.length,
      createdThisMonth: thisMonthCreated.length,
      revenueThisMonth: thisMonthRevenue
    };
  }, [mergedAcceptedItems, mergedTotalItems]);

  // Master list of ALL accepted items (ignoring global dashboard period)
  const allAcceptedItems = React.useMemo(() => {
    const items: any[] = [];

    // Filter manual leads (leads that are enrolled but don't have a corresponding proposal)
    const allManualLeads = enrolledLeads.filter(lead => {
      // Filter by owner OR branch
      const isMine = (lead.created_by === user?.id) ||
        (Array.isArray(lead.assigned_consultants) && lead.assigned_consultants.some((c: string) => c === user?.name || c === user?.id));

      const leadBranchId = lead.branch_id;
      const creatorBranchId = lead.creator?.branch_id;
      const isBranch = user?.branchId && (leadBranchId === user.branchId || creatorBranchId === user.branchId);

      if (!isMine && !isBranch) return false;

      const hasProposal = proposals.some(p => p.lead_id === lead.id);
      return !hasProposal;
    });

    // 1. Manual Leads (All)
    allManualLeads.forEach(lead => {
      items.push({
        id: `manual_${lead.id}`,
        studentName: `${lead.first_name} ${lead.last_name}`,
        university: lead.interested_universities?.[0] || 'Manuel Kayıt',
        program: lead.interested_programs?.[0] || 'Genel',
        totalAmount: parseCurrency(lead.discussed_price),
        createdAt: new Date(lead.created_at),
        respondedAt: new Date(lead.updated_at || lead.created_at),
        status: 'accepted',
        type: 'manual_lead'
      });
    });

    // 2. Proposals (All Accepted)
    proposals.forEach(proposal => {
      // Filter by owner
      const isMine = (proposal.created_by === user?.id) || (proposal.consultant_name === user?.name);

      // Improve proposal branch check if we had creator.branch_id
      const isBranch = user?.branchId && proposal.lead?.branch_id === user.branchId;

      if (proposal.status === 'accepted' && (isMine || isBranch)) {
        const student = proposal.student || {};
        items.push({
          id: proposal.id,
          studentName: proposal.student_name || `${student.name || ''} ${student.surname || ''}`.trim() || 'İsimsiz',
          university: proposal.university,
          program: proposal.program,
          totalAmount: parseCurrency(proposal.total_amount),
          createdAt: new Date(proposal.created_at),
          respondedAt: new Date(proposal.updated_at || proposal.created_at),
          status: 'accepted',
          type: 'proposal'
        });
      }
    });

    return items;
  }, [enrolledLeads, proposals]);

  // Group items by month + apply Local Date Filter
  const groupedAcceptedItems = React.useMemo(() => {
    const groups: { [key: string]: typeof allAcceptedItems } = {};

    // Filter by local date range if set
    let filtered = allAcceptedItems;
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter(item => {
        const date = new Date(item.respondedAt || item.createdAt);
        return date >= start;
      });
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(item => {
        const date = new Date(item.respondedAt || item.createdAt);
        return date <= end;
      });
    }

    // Sort descending by date
    const sorted = [...filtered].sort((a, b) =>
      (new Date(b.respondedAt || b.createdAt).getTime() - new Date(a.respondedAt || a.createdAt).getTime())
    );

    sorted.forEach(item => {
      const date = new Date(item.respondedAt || item.createdAt);
      // Group by "Month Year" (e.g. "Ocak 2025")
      const key = date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });

    return groups;
  }, [allAcceptedItems, startDate, endDate]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const loading = proposalsLoading || leadsLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Satış verileri yükleniyor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Satış Raporlarım</h2>
          <p className="text-gray-600 dark:text-gray-400">Kişisel performans analizi ve raporlarınız (Teklifler + Manuel Kayıtlar)</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-4 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="last-7-days">Son 7 Gün</option>
            <option value="last-30-days">Son 30 Gün</option>
            <option value="last-3-months">Son 3 Ay</option>
            <option value="last-6-months">Son 6 Ay</option>
            <option value="last-year">Son 1 Yıl</option>
          </select>
          <button className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors">
            <Download className="w-4 h-4" />
            <span>Rapor İndir</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Target className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalProposals}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Toplam İşlem</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.closedDealsThisMonth}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Bu Ay Kapattığım</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <TrendingUp className="w-8 h-8 text-purple-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">%{stats.conversionRate}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Dönüşüm Oranım</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Euro className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(stats.pipelineValue)}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Kayıt Tutarı</p>
            </div>
          </div>
        </div>
      </div>

      {/* Empty State or Charts */}
      {mergedAcceptedItems.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-8 text-center">
          <BarChart3 className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Henüz kabul edilen teklifiniz veya kaydınız yok</h3>
          <p className="text-gray-600 dark:text-gray-400">Teklifleriniz kabul edilmeye başladığında veya manuel kayıt yaptığınızda raporlar burada görünecek</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Performance Chart */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Aylık Performansım</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyPerformanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value, name) => [
                  name === 'revenue' ? formatCurrency(Number(value)) : value,
                  name === 'proposals' ? 'İşlem' : name === 'accepted' ? 'Kayıt' : 'Gelir'
                ]} />
                <Bar dataKey="proposals" fill="#3B82F6" name="proposals" />
                <Bar dataKey="accepted" fill="#10B981" name="accepted" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Accepted Proposals and Registrations Grouped by Month */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Kabul Edilen Teklifler ve Kayıtlar</h3>

              {/* Date Filter Inputs */}
              <div className="flex items-center gap-2 text-sm">
                <div className="relative">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="pl-3 pr-2 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="Başlangıç"
                  />
                </div>
                <span className="text-gray-400">-</span>
                <div className="relative">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="pl-3 pr-2 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="Bitiş"
                  />
                </div>
                {(startDate || endDate) && (
                  <button
                    onClick={() => { setStartDate(''); setEndDate(''); }}
                    className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                    title="Filtreyi Temizle"
                  >
                    X
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-8 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
              {Object.entries(groupedAcceptedItems).map(([month, items]) => (
                <div key={month} className="relative">
                  <div className="sticky top-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm z-10 py-2 border-b border-gray-100 dark:border-gray-700 mb-3">
                    <h4 className="text-sm font-bold text-gray-500 dark:text-gray-400 flex items-center justify-between">
                      <span>{month}</span>
                      <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full">
                        {items.length} Kayıt
                      </span>
                    </h4>
                  </div>
                  <div className="space-y-3">
                    {items.map((item, index) => (
                      <div key={item.id || index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-white hover:shadow-md dark:hover:bg-gray-750 transition-all duration-200 border border-transparent hover:border-gray-100 dark:hover:border-gray-700 group">
                        <div className="flex-1 min-w-0 mr-4">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-gray-900 dark:text-white truncate">{item.studentName}</p>
                            {item.type === 'manual_lead' && (
                              <span className="text-[10px] px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full font-medium whitespace-nowrap">
                                Manuel
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                            {item.university}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500 dark:text-gray-500">
                              {item.program}
                            </span>
                            <span className="text-gray-300 dark:text-gray-600">•</span>
                            <span className="text-xs text-gray-500 dark:text-gray-500">
                              {item.respondedAt ? new Date(item.respondedAt).toLocaleDateString('tr-TR') : new Date(item.createdAt).toLocaleDateString('tr-TR')}
                            </span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-bold text-green-600 dark:text-green-400 text-lg">
                            {formatCurrency(parseCurrency(item.totalAmount))}
                          </p>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 mt-1">
                            {item.type === 'manual_lead' ? 'Kayıt Oldu' : 'Kabul Edildi'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Performance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Bu Ay Özetim</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">İşlem Hacmi (Teklif + Kayıt)</span>
              <span className="font-bold text-gray-900 dark:text-white">
                {stats.createdThisMonth}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Kapattığım Anlaşmalar</span>
              <span className="font-bold text-green-600 dark:text-green-400">{stats.closedDealsThisMonth}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Toplam Gelirim</span>
              <span className="font-bold text-gray-900 dark:text-white">
                {formatCurrency(stats.revenueThisMonth)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Başarı Oranım</span>
              <span className="font-bold text-purple-600 dark:text-purple-400">%{stats.conversionRate}</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Hedef Durumum</h4>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600 dark:text-gray-400">Aylık İşlem Hedefim</span>
                <span className="text-gray-900 dark:text-white">
                  {stats.createdThisMonth}/15
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{
                    width: `${Math.min((stats.createdThisMonth / 15) * 100, 100)}%`
                  }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600 dark:text-gray-400">Gelir Hedefim</span>
                <span className="text-gray-900 dark:text-white">
                  {formatCurrency(stats.revenueThisMonth)}/€50,000
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{
                    width: `${Math.min((stats.revenueThisMonth / 50000) * 100, 100)}%`
                  }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600 dark:text-gray-400">Anlaşma Hedefim</span>
                <span className="text-gray-900 dark:text-white">{stats.closedDealsThisMonth}/5</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-purple-500 h-2 rounded-full"
                  style={{ width: `${Math.min((stats.closedDealsThisMonth / 5) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>



    </div >
  );
};

export default SalesReports;